import React, { useEffect, useState } from 'react';
import {
  Box, List, ListItemText, Typography, ListItemButton, ListItemIcon,
  Drawer, Divider, Chip, Stack, Tooltip,
} from '@mui/material';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import WifiIcon from '@mui/icons-material/Wifi';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import { getSocket } from '../socket';
import { useAuth } from '../auth/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import versionInfo from '../version.json';

export const SIDEBAR_WIDTH = 248;

const navItems = [
  { label: 'Dashboard', href: '/', icon: <DashboardOutlinedIcon fontSize="small" /> },
  { label: 'Device Control', href: '/device', icon: <TuneOutlinedIcon fontSize="small" /> },
  { label: 'History', href: '/history', icon: <HistoryOutlinedIcon fontSize="small" /> },
  { label: 'Insights', href: '/analytics', icon: <InsightsOutlinedIcon fontSize="small" /> },
];

const SidebarContent = ({ onNavigate }) => {
  const { token, role, logout } = useAuth();
  const [device, setDevice] = useState({ id: '', online: false, link: 'wifi' });
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const socket = getSocket();
    const onStatus = (data) => {
      if (data && data.deviceId) {
        setDevice({ id: data.deviceId, online: !!data.online, link: data.link || 'wifi' });
      }
    };
    socket.on('device-status', onStatus);
    return () => socket.off('device-status', onStatus);
  }, []);

  const go = (href) => { navigate(href); if (onNavigate) onNavigate(); };
  const isActive = (href) => (href === '/' ? location.pathname === '/' : location.pathname.startsWith(href));

  const items = [...navItems];
  if (role === 'admin' || role === 'superadmin') {
    items.push({ label: 'Admin Panel', href: '/admin', icon: <AdminPanelSettingsOutlinedIcon fontSize="small" /> });
  }

  return (
    <Box sx={{ width: SIDEBAR_WIDTH, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Brand */}
      <Box sx={{ px: 2.5, py: 2.25, display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box sx={{
          width: 34, height: 34, borderRadius: 2, display: 'grid', placeItems: 'center',
          background: 'linear-gradient(135deg, #2563eb, #22d3ee)', color: '#fff', flexShrink: 0,
        }}>
          <ScienceOutlinedIcon fontSize="small" />
        </Box>
        <Box sx={{ lineHeight: 1 }}>
          <Typography sx={{ fontWeight: 800, letterSpacing: '0.02em', fontSize: '1.05rem' }}>AQUATRON</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: '0.06em' }}>
            IoT ANALYSIS PLATFORM
          </Typography>
        </Box>
      </Box>
      <Divider />

      {/* Live device status */}
      <Box sx={{ px: 2, py: 1.75 }}>
        <Typography variant="overline" color="text.secondary">Live Device</Typography>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
          <Box sx={{
            width: 9, height: 9, borderRadius: '50%',
            bgcolor: device.online ? 'success.main' : 'text.disabled',
            boxShadow: device.online ? (t) => `0 0 0 3px ${t.palette.success.main}33` : 'none',
          }} />
          <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }} noWrap>
            {device.id ? (device.id.length > 14 ? device.id.slice(0, 14) + '…' : device.id) : 'No device online'}
          </Typography>
          {device.id && (
            <Tooltip title={device.link === '4g' ? 'Cellular / 4G uplink' : 'WiFi uplink'}>
              <Chip
                size="small" variant="outlined"
                icon={device.link === '4g' ? <SignalCellularAltIcon sx={{ fontSize: 14 }} /> : <WifiIcon sx={{ fontSize: 14 }} />}
                label={device.link === '4g' ? '4G' : 'WiFi'}
                color={device.online ? 'primary' : 'default'}
              />
            </Tooltip>
          )}
        </Stack>
      </Box>
      <Divider />

      {/* Nav */}
      <List sx={{ flex: 1, py: 1.5 }}>
        {items.map((item) => (
          <ListItemButton key={item.href} selected={isActive(item.href)} onClick={() => go(item.href)}>
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 600, fontSize: '0.92rem' }} />
          </ListItemButton>
        ))}
      </List>

      {/* Footer */}
      <Divider />
      <Box sx={{ p: 1.5 }}>
        {token && (
          <>
            <ListItemButton selected={isActive('/preference')} onClick={() => go('/preference')}>
              <ListItemIcon><SettingsOutlinedIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Preferences" primaryTypographyProps={{ fontWeight: 600, fontSize: '0.92rem' }} />
            </ListItemButton>
            <ListItemButton onClick={() => { logout(); go('/login'); }}>
              <ListItemIcon><LogoutOutlinedIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Log out" primaryTypographyProps={{ fontWeight: 600, fontSize: '0.92rem' }} />
            </ListItemButton>
          </>
        )}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1.5, pt: 1 }}>
          <Typography variant="caption" color="text.secondary">v{versionInfo.version}</Typography>
          <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>{versionInfo.commit}</Typography>
        </Stack>
        <Box sx={{ textAlign: 'center', pt: 0.75 }}>
          <Typography variant="caption" color="text.secondary">
            Powered by{' '}
            <Typography
              component="a" variant="caption"
              href="https://www.wirelessmind.in" target="_blank" rel="noopener noreferrer"
              sx={{ color: 'primary.main', fontWeight: 700, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              Wirelessmind
            </Typography>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// mobileOpen / onClose let the TopBar's menu button drive the temporary drawer.
const Sidebar = ({ mobileOpen = false, onClose = () => {} }) => (
  <>
    <Drawer
      variant="temporary"
      open={mobileOpen}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH } }}
    >
      <SidebarContent onNavigate={onClose} />
    </Drawer>
    <Drawer
      variant="permanent"
      open
      sx={{ display: { xs: 'none', md: 'block' }, '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH } }}
    >
      <SidebarContent />
    </Drawer>
  </>
);

export default Sidebar;
